import assert from "assert";
import { UrlObject } from "url";
import { CrumbRule } from "components/elements/Breadcrumbs/crumb-rule";
import { CrumbComponent } from "./crumb-component";
import BreadcrumbDefault from "./renderers/BreadcrumbDefault";

export function generateCrumbs(
  rules: CrumbRule[],
  pathname: string,
  query: Record<string, string | string[]>,
) {
  assert(rules);
  assert(query);

  const withMatchers = rules
    .filter((r) => r.segments?.length > 0)
    .map((r) => ({
      ...r,
      matcher: new RegExp(
        "^" + r.segments.map((rgx) => "(" + rgx.source + ")").join("\\/"),
        "i",
      ),
    }));

  let crumbs: {
    component: CrumbComponent;
    segments: string[];
    params: string[];
    href: UrlObject;
  }[] = [];
  let unmatched = pathname;
  let hrefBase = {
    pathname: "",
    query: {},
  };
  while (true) {
    if (unmatched.startsWith("/")) unmatched = unmatched.slice(1);
    if (unmatched?.length < 1) break;

    let highestPriorityMatch: (typeof withMatchers)[0] & {
      match: RegExpMatchArray;
    };
    for (let rule of withMatchers) {
      const r = unmatched.match(rule.matcher);
      if (
        r &&
        (!highestPriorityMatch ||
          r[0].length > highestPriorityMatch.match.length)
      ) {
        highestPriorityMatch = { ...rule, match: r };
      }
    }

    let match: string;
    if (highestPriorityMatch) {
      match = highestPriorityMatch.match[0];
      const captures = highestPriorityMatch.match.slice(1);
      const params = readParams(captures, query);
      const href = (hrefBase = appendHref(hrefBase, match, params));
      crumbs.push({
        component: highestPriorityMatch.component,
        segments: captures,
        params: Object.values(params),
        href,
      });
    } else {
      match = unmatched.split("/", 1)[0];
      const segments = [match];
      const params = readParams(segments, query);
      const href = (hrefBase = appendHref(hrefBase, match, params));
      crumbs.push({
        component: BreadcrumbDefault,
        segments,
        params: Object.values(params),
        href,
      });
    }
    unmatched = unmatched.slice(match.length);
  }

  return crumbs.filter((x) => !!x.component);
}

export function readParams(
  segments: string[],
  query: Record<string, string | string[]>,
): Record<string, string> {
  assert(segments);
  assert(query);
  return segments
    .map((s) => s.match(/\[(\w+)\]/i)?.[1])
    .filter((key) => !!key)
    .reduce(
      (o, key) => (
        (o[key] = Array.isArray(query[key]) ? query[key][0] : query[key]), o
      ),
      {},
    );
}

export function appendHref(
  base: { pathname: string; query: Record<string, string> },
  match: string,
  params: Record<string, string>,
): { pathname: string; query: Record<string, string> } {
  assert(base);
  return {
    pathname: base.pathname + "/" + match,
    query: {
      ...base.query,
      ...params,
    },
  };
}
